import { useEffect, useState, type ChangeEvent, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit, IconTrash } from '../../components/icons';
import { Modal } from '../../components/Modal';
import { useConfirm } from '../../context/ConfirmContext';
import { useToast } from '../../context/ToastContext';

interface PositionOption {
  id: number;
  position_code: string;
  position_title: string;
}

interface PerformanceKra {
  id: number;
  position_id: number;
  name: string;
}

interface PerformanceKpi {
  id: number;
  position_id: number;
  position: PositionOption | null;
  performance_kra_id: number | null;
  performance_kra: { id: number; name: string } | null;
  name: string;
  weight: string;
}

interface KpiForm {
  position_id: string | number;
  performance_kra_id: string | number;
  name: string;
  weight: string;
}

const emptyForm: KpiForm = { position_id: '', performance_kra_id: '', name: '', weight: '' };

export function PerformanceKpis() {
  const confirmDelete = useConfirm();
  const { showError } = useToast();
  const [items, setItems] = useState<PerformanceKpi[] | null>(null);
  const [positions, setPositions] = useState<PositionOption[]>([]);
  const [kras, setKras] = useState<PerformanceKra[]>([]);
  const [editing, setEditing] = useState<'new' | PerformanceKpi | null>(null);
  const [form, setForm] = useState<KpiForm>(emptyForm);
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<PerformanceKpi[]>('/performance-kpis').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    api.get<PositionOption[]>('/positions').then((res) => setPositions(res.data));
    api.get<PerformanceKra[]>('/performance-kras').then((res) => setKras(res.data));
  }, []);

  const openNew = () => {
    setForm(emptyForm);
    setErrors({});
    setEditing('new');
  };

  const openEdit = (item: PerformanceKpi) => {
    setForm({
      position_id: item.position_id,
      performance_kra_id: item.performance_kra_id ?? '',
      name: item.name,
      weight: item.weight,
    });
    setErrors({});
    setEditing(item);
  };

  const set =
    (key: keyof KpiForm) =>
    (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
      if (key === 'position_id') {
        // Changing position invalidates any previously-chosen KRA, since
        // KRA options are scoped to the position.
        setForm({ ...form, position_id: e.target.value, performance_kra_id: '' });
      } else {
        setForm({ ...form, [key]: e.target.value });
      }
    };

  const krasForPosition = kras.filter((k) => String(k.position_id) === String(form.position_id));

  const save = async (e: FormEvent) => {
    e.preventDefault();
    try {
      if (editing === 'new') await api.post('/performance-kpis', form);
      else if (editing) await api.put(`/performance-kpis/${editing.id}`, form);
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  const remove = async (item: PerformanceKpi) => {
    if (!(await confirmDelete(`Delete the KPI "${item.name}" for "${item.position?.position_title}"?`))) return;
    try {
      await api.delete(`/performance-kpis/${item.id}`);
      load();
    } catch (err) {
      showError(err instanceof ApiError ? err.message : 'Delete failed.');
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Performance KPIs</h1>
        <button type="button" className="btn btn-primary" onClick={openNew}>
          + Add
        </button>
      </header>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Position</th>
                <th>KRA</th>
                <th>KPI Name</th>
                <th>Weight</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.position?.position_title ?? '—'}</td>
                  <td className="muted">{item.performance_kra?.name ?? 'Other'}</td>
                  <td>{item.name}</td>
                  <td className="muted">{item.weight}%</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                    <IconButton icon={IconTrash} label="Delete" variant="danger-ghost" onClick={() => remove(item)} />
                  </td>
                </tr>
              ))}
              {items?.length === 0 && (
                <tr>
                  <td colSpan={5} className="muted center">
                    No performance KPIs configured yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={editing === 'new' ? 'Add performance KPI' : 'Edit performance KPI'} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            <label className="span-2">
              Position *
              <select value={form.position_id} onChange={set('position_id')} required>
                <option value="">Select…</option>
                {positions.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.position_code} — {p.position_title}
                  </option>
                ))}
              </select>
              {errors.position_id && <small className="error">{errors.position_id[0]}</small>}
            </label>
            <label className="span-2">
              Key Result Area
              <select value={form.performance_kra_id} onChange={set('performance_kra_id')} disabled={!form.position_id}>
                <option value="">Other (ungrouped)</option>
                {krasForPosition.map((k) => (
                  <option key={k.id} value={k.id}>
                    {k.name}
                  </option>
                ))}
              </select>
              <small className="muted">Optional. When set, this KPI's weight is normalized within the KRA, not across the whole position.</small>
              {errors.performance_kra_id && <small className="error">{errors.performance_kra_id[0]}</small>}
            </label>
            <label className="span-2">
              KPI Name *
              <input value={form.name} onChange={set('name')} required />
              {errors.name && <small className="error">{errors.name[0]}</small>}
            </label>
            <label className="span-2">
              Weight (%) *
              <input type="number" step="0.1" min="0" max="100" value={form.weight} onChange={set('weight')} required />
              {errors.weight && <small className="error">{errors.weight[0]}</small>}
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
